import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faSearch, faTimes, faCheck } from '@fortawesome/free-solid-svg-icons';

interface Supplier {
  _id: string;
  name: string;
  kreditor?: string;
  gln?: string;
  isActive?: boolean;
  masterSupplierId?: string;
}

interface SupplierMultiSelectProps {
  suppliers: Supplier[];
  selectedSupplierIds: string[];
  onChange: (supplierIds: string[]) => void;
  placeholder?: string;
  className?: string;
}

const SupplierMultiSelect: React.FC<SupplierMultiSelectProps> = ({
  suppliers,
  selectedSupplierIds,
  onChange,
  placeholder = "Alle Lieferanten",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter aktive Lieferanten (nur Hauptlieferanten, keine verknüpften) und nach Suchterm
  const filteredSuppliers = suppliers
    .filter(supplier => supplier.isActive !== false)
    .filter(supplier => !supplier.masterSupplierId) // Keine verknüpften Lieferanten anzeigen
    .filter(supplier => 
      supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (supplier.kreditor && supplier.kreditor.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (supplier.gln && supplier.gln.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const selectedSuppliers = suppliers.filter(supplier => selectedSupplierIds.includes(supplier._id));

  const handleToggleSupplier = (supplierId: string) => {
    if (selectedSupplierIds.includes(supplierId)) {
      onChange(selectedSupplierIds.filter(id => id !== supplierId));
    } else {
      onChange([...selectedSupplierIds, supplierId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedSupplierIds.length === filteredSuppliers.length && filteredSuppliers.length > 0) {
      onChange([]);
    } else {
      onChange(filteredSuppliers.map(supplier => supplier._id));
    }
  };

  const getDisplayText = () => {
    if (selectedSupplierIds.length === 0) {
      return placeholder;
    }
    
    if (selectedSupplierIds.length === 1) {
      const supplier = selectedSuppliers[0];
      return supplier ? `${supplier.name}` : placeholder;
    }
    
    if (selectedSupplierIds.length <= 2) {
      return selectedSuppliers.map(supplier => supplier.name).join(', ');
    }
    
    return `${selectedSupplierIds.length} Lieferanten`;
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent bg-white text-sm text-left flex items-center justify-between min-w-[180px]"
      >
        <span className={`truncate ${selectedSupplierIds.length === 0 ? 'text-gray-500' : 'text-gray-900'}`}>
          {getDisplayText()}
        </span>
        <FontAwesomeIcon 
          icon={faChevronDown} 
          className={`ml-2 h-4 w-4 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Lieferant suchen..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                onClick={(e) => e.stopPropagation()}
              />
              {searchTerm && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearchTerm('');
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Select All Option */}
          {filteredSuppliers.length > 0 && (
            <div className="border-b border-gray-200">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectAll();
                }}
                className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center text-sm font-medium text-purple-600"
              >
                <div className="flex items-center justify-center w-4 h-4 mr-3 border border-purple-300 rounded">
                  {selectedSupplierIds.length === filteredSuppliers.length && filteredSuppliers.length > 0 && (
                    <FontAwesomeIcon icon={faCheck} className="h-3 w-3 text-purple-600" />
                  )}
                </div>
                {selectedSupplierIds.length === filteredSuppliers.length && filteredSuppliers.length > 0 ? 'Alle abwählen' : 'Alle auswählen'}
              </button>
            </div>
          )}

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {filteredSuppliers.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                {searchTerm ? 'Keine Lieferanten gefunden' : 'Keine Lieferanten verfügbar'}
              </div>
            ) : (
              filteredSuppliers.map(supplier => (
                <button
                  key={supplier._id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleSupplier(supplier._id);
                  }}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center text-sm"
                >
                  <div className="flex items-center justify-center w-4 h-4 mr-3 border border-gray-300 rounded">
                    {selectedSupplierIds.includes(supplier._id) && (
                      <FontAwesomeIcon icon={faCheck} className="h-3 w-3 text-purple-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">{supplier.name}</div>
                    {(supplier.kreditor || supplier.gln) && (
                      <div className="text-xs text-gray-500 truncate">
                        {supplier.kreditor || supplier.gln}
                      </div>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierMultiSelect;